import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  forwardRef,
  HostBinding,
  ElementRef,
  ViewChild,
  AfterViewInit,
  Injector,
  DoCheck,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, NgControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { MatFormFieldControl } from '@angular/material/form-field';
import { NgClass } from '@angular/common';

@Component({
  selector: 'txp-rich-text-editor',
  template: `
    <div
      #editorContainer
      (click)="onTouched()"
      [ngClass]="{ 'empty-editor': isEmptyAndNotFocused() }"
    ></div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RichTextEditorComponent),
      multi: true,
    },
    {
      provide: MatFormFieldControl,
      useExisting: RichTextEditorComponent,
    },
  ],
  host: {
    '[id]': 'id',
    '[attr.aria-describedby]': 'describedBy',
  },
  styleUrls: ['./rich-text-editor.component.scss'],
  standalone: true,
  imports: [NgClass],
})
export class RichTextEditorComponent
  implements
    OnInit,
    AfterViewInit,
    OnDestroy,
    DoCheck,
    ControlValueAccessor,
    MatFormFieldControl<any>
{
  static nextId = 0;
  @HostBinding() id = `txp-rich-text-editor-${RichTextEditorComponent.nextId++}`;

  stateChanges = new Subject<void>();
  focused = false;
  controlType = 'txp-rich-text-editor';
  errorState = false;
  ngControl: NgControl | null = null;
  touched = false;
  editor: any;
  _value: any;

  @ViewChild('editorContainer', { static: true }) editorContainer: ElementRef;

  @Input() tinyMceConfig: any = {
    height: 300,
    menubar: false,
    plugins: 'link image code lists',
    toolbar:
      'undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat',
  };

  @Input()
  get placeholder() {
    return this._placeholder;
  }
  set placeholder(plh) {
    this._placeholder = plh;
    this.stateChanges.next();
  }
  private _placeholder: string;

  @Input()
  get required() {
    return this._required;
  }
  set required(req) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }
  private _required = false;

  @Input()
  get disabled() {
    return this._disabled;
  }
  set disabled(disabled) {
    this._disabled = coerceBooleanProperty(disabled);
    this.stateChanges.next();
  }
  private _disabled = false;

  get value(): any {
    return this._value;
  }
  set value(value) {
    this._value = value;
    if (this.editor) {
      this.editor.setContent(value || '');
    }
    this.onChange(value);
    this.stateChanges.next();
  }

  constructor(
    public elRef: ElementRef,
    public fm: FocusMonitor,
    public injector: Injector,
  ) {
    fm.monitor(elRef.nativeElement, true).subscribe((origin) => {
      this.focused = !!origin;
      this.stateChanges.next();
    });
  }

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null);
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngAfterViewInit(): void {
    this.loadTinyMCE();
  }

  ngOnDestroy() {
    this.stateChanges.complete();
    this.fm.stopMonitoring(this.elRef.nativeElement);
    if (this.editor) {
      this.editor.remove();
    }
  }

  ngDoCheck(): void {
    if (this.ngControl) {
      this.errorState = this.ngControl.invalid && this.touched;
      this.stateChanges.next();
    }
  }

  public loadTinyMCE(): void {
    if ((window as any).tinymce) {
      this.initializeEditor();
    }
  }

  public initializeEditor(): void {
    (window as any).tinymce
      .init({
        target: this.editorContainer.nativeElement,
        ...this.defaultConfig,
        ...this.tinyMceConfig,
      })
      .then((editors: any) => {
        this.editor = editors[0];
      });
  }

  private defaultConfig: any = {
    base_url: '/tinymce',
    suffix: '.min',
    license_key: 'gpl',
    setup: (editor: any) => {
      editor.on('init', () => {
        if (this._value) {
          editor.setContent(this._value);
        }
      });
      editor.on('change', () => {
        this._value = editor.getContent();
        this.onChange(this._value);
      });
      editor.on('focus', () => {
        this.stateChanges.next();
      });
      editor.on('blur', () => {
        this.touched = true;
        this.stateChanges.next();
      });
    },
  };

  writeValue(value: any): void {
    this.value = value;
  }

  onChange = (value: any) => {};

  registerOnChange(fn: (v: any) => void): void {
    this.onChange = fn;
  }

  onTouched = () => {
    this.touched = true;
  };

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onContainerClick(event: MouseEvent): void {
    if (!this.focused) {
      this.editor.focus();
      this.stateChanges.next();
    }
  }

  isEmptyAndNotFocused(): boolean {
    return this.empty && !this.focused;
  }

  get empty() {
    return !this.editor || !this.editor.getContent().trim();
  }

  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  @HostBinding('attr.aria-describedby') describedBy = '';
  setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(' ');
  }
}
